/**
 * Tool Locking Admin Panel
 *
 * Lets admins enable/disable any of the 13 COMPANY_PERMISSIONS feature flags
 * for any company — without opening the Companies modal.
 *
 * Architecture:
 *  - Loads all companies from Redux (same source as CompaniesTable)
 *  - Per-toggle optimistic update with automatic rollback on API failure
 *  - Per-toggle loading state so one in-flight request never blocks others
 *  - Search + subscription-tier filter so admins can find companies fast
 *  - Zero new backend endpoints — reuses companyService.updateCompanyPermissions
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { IApplicationState } from "../../redux/reducers";
import { ICompany } from "../../interfaces/company";
import { COMPANY_PERMISSIONS } from "../../interfaces/permissions";
import companyService from "../../services/companyService";
import notification from "../../utils/notification";
import { toCamelCase } from "../../utils/string";
import "../../assets/scss/pages/admin/_toolLocking.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

/** Human-readable labels for each permission key */
const PERMISSION_LABELS: Record<COMPANY_PERMISSIONS, string> = {
  [COMPANY_PERMISSIONS.PACKAGING]:               "Packaging",
  [COMPANY_PERMISSIONS.INGREDIENT_ENGINE]:       "Ingredient Engine",
  [COMPANY_PERMISSIONS.PRODUCT_TOOLS]:           "Product Tools",
  [COMPANY_PERMISSIONS.PACKAGING_ENGINE]:        "Packaging Engine",
  [COMPANY_PERMISSIONS.COST_INSIGHTS]:           "Cost Insights",
  [COMPANY_PERMISSIONS.REGULATORY_INSIGHTS]:     "Regulatory Insights",
  [COMPANY_PERMISSIONS.SUSTAINABILITY_INSIGHTS]: "Sustainability Insights",
  [COMPANY_PERMISSIONS.PERSONA_ENGINE]:          "Persona Engine",
  [COMPANY_PERMISSIONS.INGREDIENT_DATASHEET]:    "Ingredient Datasheet",
  [COMPANY_PERMISSIONS.JOURNEY_AI]:              "Journey AI",
  [COMPANY_PERMISSIONS.CUSTOM_REPORTING]:        "Custom Reporting",
  [COMPANY_PERMISSIONS.INTEGRATION_REQUEST]:     "Integration Request",
  [COMPANY_PERMISSIONS.LABELS_TOOL]:             "Labels Tool",
};

const ALL_PERMISSIONS = Object.values(COMPANY_PERMISSIONS);

const SUBSCRIPTION_OPTIONS = [
  "all", "legacy", "supplier", "fresh", "growth", "enterprise", "group", "guava", ""
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface IProps {
  companies: ICompany[];
  getCompanies: () => Promise<any>;
  updateCompanyPermissions: (
    company: ICompany,
    permission: { [key: string]: string }
  ) => Promise<any>;
}

// Key: `${companyId}__${permission}` → true while PATCH is in-flight
type LoadingMap = Record<string, boolean>;

// ─── Component ────────────────────────────────────────────────────────────────

const ToolLocking: React.FC<IProps> = ({ companies, getCompanies, updateCompanyPermissions }) => {
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [togglingMap, setTogglingMap] = useState<LoadingMap>({});

  // Local optimistic copy of companies so UI updates instantly
  const [localCompanies, setLocalCompanies] = useState<ICompany[]>([]);

  // Abort controller ref — cancel in-flight requests on unmount
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getCompanies().finally(() => {
      if (mounted) setLoading(false);
    });
    return () => {
      mounted = false;
      abortRef.current?.abort();
    };
  }, []);

  // Sync local copy whenever Redux companies change
  useEffect(() => {
    setLocalCompanies(companies ?? []);
  }, [companies]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const companyHasPermission = (company: ICompany, permission: COMPANY_PERMISSIONS): boolean => {
    const key = permission.toLowerCase();
    return company.hasOwnProperty(key) && company[key] === "enable";
  };

  const toggleKey = (companyId: string, permission: COMPANY_PERMISSIONS) =>
    `${companyId}__${permission}`;

  const isToggling = (companyId: string, permission: COMPANY_PERMISSIONS) =>
    !!togglingMap[toggleKey(companyId, permission)];

  // ── Toggle handler ─────────────────────────────────────────────────────────

  const handleToggle = async (company: ICompany, permission: COMPANY_PERMISSIONS) => {
    const key = toggleKey(company.id, permission);
    if (togglingMap[key]) return; // already in-flight

    const permKey = permission.toLowerCase();
    const currentlyEnabled = companyHasPermission(company, permission);
    const newValue = currentlyEnabled ? "disable" : "enable";

    // 1. Optimistic update
    setLocalCompanies((prev) =>
      prev.map((c) =>
        c.id === company.id ? { ...c, [permKey]: newValue } : c
      )
    );
    setTogglingMap((prev) => ({ ...prev, [key]: true }));

    try {
      await updateCompanyPermissions(company, { [permKey]: newValue });
      // Success — optimistic state is already correct, nothing more to do
    } catch {
      // 2. Rollback on failure
      setLocalCompanies((prev) =>
        prev.map((c) =>
          c.id === company.id
            ? { ...c, [permKey]: currentlyEnabled ? "enable" : "disable" }
            : c
        )
      );
      notification.error(`Failed to update ${PERMISSION_LABELS[permission]} for ${company.name}`);
    } finally {
      setTogglingMap((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // ── Filtered list ──────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let result = localCompanies;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q)
      );
    }

    if (tierFilter !== "all") {
      result = result.filter(
        (c) => (c.subscription_type ?? "") === tierFilter
      );
    }

    return result;
  }, [localCompanies, search, tierFilter]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="tool-locking__loading">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading…</span>
        </div>
        <p>Loading companies…</p>
      </div>
    );
  }

  return (
    <div className="tool-locking">
      {/* ── Header ── */}
      <div className="tool-locking__header">
        <div>
          <h3>Tool Locking</h3>
          <p>Enable or disable feature access per company. Changes take effect immediately.</p>
        </div>
        <div className="tool-locking__stats">
          <span className="tool-locking__stat">
            <strong>{localCompanies.length}</strong> companies
          </span>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="tool-locking__filters">
        <div className="tool-locking__search-wrap">
          <i className="fas fa-search tool-locking__search-icon" />
          <input
            type="text"
            className="tool-locking__search"
            placeholder="Search by company name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search companies"
          />
          {search && (
            <button
              className="tool-locking__search-clear"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              type="button"
            >
              <i className="fas fa-times" />
            </button>
          )}
        </div>

        <select
          className="tool-locking__tier-filter"
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          aria-label="Filter by subscription tier"
        >
          {SUBSCRIPTION_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t === "all" ? "All tiers" : t === "" ? "No tier" : toCamelCase(t)}
            </option>
          ))}
        </select>
      </div>

      {/* ── Results count ── */}
      {search || tierFilter !== "all" ? (
        <p className="tool-locking__result-count">
          Showing {filtered.length} of {localCompanies.length} companies
        </p>
      ) : null}

      {/* ── Empty state ── */}
      {filtered.length === 0 && (
        <div className="tool-locking__empty">
          <i className="fas fa-building tool-locking__empty-icon" />
          <p>No companies match your filters.</p>
        </div>
      )}

      {/* ── Company cards ── */}
      <div className="tool-locking__list">
        {filtered.map((company) => (
          <CompanyPermissionCard
            key={company.id}
            company={company}
            permissions={ALL_PERMISSIONS}
            permissionLabels={PERMISSION_LABELS}
            companyHasPermission={companyHasPermission}
            isToggling={isToggling}
            onToggle={handleToggle}
          />
        ))}
      </div>
    </div>
  );
};

// ─── CompanyPermissionCard ────────────────────────────────────────────────────

interface CardProps {
  company: ICompany;
  permissions: COMPANY_PERMISSIONS[];
  permissionLabels: Record<COMPANY_PERMISSIONS, string>;
  companyHasPermission: (c: ICompany, p: COMPANY_PERMISSIONS) => boolean;
  isToggling: (companyId: string, p: COMPANY_PERMISSIONS) => boolean;
  onToggle: (c: ICompany, p: COMPANY_PERMISSIONS) => void;
}

const CompanyPermissionCard: React.FC<CardProps> = ({
  company,
  permissions,
  permissionLabels,
  companyHasPermission,
  isToggling,
  onToggle,
}) => {
  const [expanded, setExpanded] = useState(false);

  const enabledCount = permissions.filter((p) => companyHasPermission(company, p)).length;

  return (
    <div className="tool-locking__card">
      {/* Card header — always visible */}
      <button
        className="tool-locking__card-header"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        type="button"
      >
        <div className="tool-locking__card-meta">
          <span className="tool-locking__company-name">{company.name || "—"}</span>
          <span className="tool-locking__company-email">{company.email}</span>
        </div>
        <div className="tool-locking__card-right">
          {company.subscription_type && (
            <span className={`tool-locking__tier-badge tool-locking__tier-badge--${company.subscription_type}`}>
              {toCamelCase(company.subscription_type)}
            </span>
          )}
          <span className="tool-locking__enabled-count">
            {enabledCount}/{permissions.length} enabled
          </span>
          <i className={`fas fa-chevron-${expanded ? "up" : "down"} tool-locking__chevron`} />
        </div>
      </button>

      {/* Toggles — only rendered when expanded */}
      {expanded && (
        <div className="tool-locking__toggles">
          {permissions.map((permission) => {
            const enabled = companyHasPermission(company, permission);
            const busy = isToggling(company.id, permission);

            return (
              <div key={permission} className="tool-locking__toggle-row">
                <span className="tool-locking__toggle-label">
                  {permissionLabels[permission]}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  aria-label={`${permissionLabels[permission]} for ${company.name}`}
                  disabled={busy}
                  className={`tool-locking__toggle ${enabled ? "tool-locking__toggle--on" : "tool-locking__toggle--off"} ${busy ? "tool-locking__toggle--busy" : ""}`}
                  onClick={() => onToggle(company, permission)}
                >
                  {busy ? (
                    <span className="tool-locking__toggle-spinner" />
                  ) : (
                    <span className="tool-locking__toggle-thumb" />
                  )}
                </button>
                <span className={`tool-locking__toggle-status ${enabled ? "tool-locking__toggle-status--on" : "tool-locking__toggle-status--off"}`}>
                  {enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Redux ────────────────────────────────────────────────────────────────────

const mapStateToProps = (state: IApplicationState) => ({
  companies: state.company.companies ?? [],
});

const mapDispatchToProps = {
  getCompanies: () => companyService.getCompanies(),
  updateCompanyPermissions: (company: ICompany, permission: { [key: string]: string }) =>
    companyService.updateCompanyPermissions(company, permission),
};

export default connect(mapStateToProps, mapDispatchToProps)(ToolLocking);
